import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Eye, EyeOff, Images, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { PageTitle } from "../../components/admin/Field";
import { RoomForm } from "../../components/admin/RoomForm";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../components/ui/alert-dialog";

const Btn = ({ children, testid, danger, ...rest }) => (
  <button data-testid={testid} className={`h-11 px-4 inline-flex items-center gap-2 rounded-full border border-zinc-700 text-sm ${danger ? "hover:border-red-400 hover:text-red-300" : "hover:border-[#D4AF37] hover:text-[#E6C687]"}`} {...rest}>{children}</button>
);

export default function Rooms() {
  const [rooms, setRooms] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(() => api.get("/admin/accommodations").then((r) => setRooms(r.data)), []);
  useEffect(() => {
    load();
  }, [load]);

  const edit = (r) => {
    setEditing(r);
    setOpen(true);
  };

  const togglePublish = async (r) => {
    const { id, slug, created_at, updated_at, photo_count, ...body } = r;
    try {
      await api.put(`/admin/accommodations/${id}`, { ...body, published: !r.published });
      toast.success(r.published ? "Acomodação ocultada" : "Acomodação publicada");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async () => {
    await api.delete(`/admin/accommodations/${deleting.id}`);
    toast.success("Acomodação excluída");
    setDeleting(null);
    load();
  };

  return (
    <div data-testid="admin-rooms">
      <PageTitle title="Acomodações" desc="Cadastre quartos e suítes. Somente acomodações publicadas aparecem no site.">
        <button onClick={() => edit(null)} data-testid="room-new-btn" className="btn-gold h-12"><Plus size={18} /> Nova acomodação</button>
      </PageTitle>
      <div className="space-y-4">
        {rooms.length === 0 && <p className="text-zinc-500" data-testid="rooms-empty">Nenhuma acomodação cadastrada.</p>}
        {rooms.map((r) => (
          <div key={r.id} data-testid={`admin-room-${r.slug}`} className="bg-[#0A0A0A] border border-zinc-800/80 rounded-lg p-5 flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className="font-display text-2xl">{r.name}</h3>
                <span data-testid={`room-status-${r.slug}`} className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded ${r.published ? "bg-emerald-500/15 text-emerald-300" : "bg-zinc-800 text-zinc-400"}`}>{r.published ? "Publicada" : "Oculta"}</span>
              </div>
              <p className="text-sm text-zinc-500 mt-1">{r.photo_count} foto(s){r.price && ` · ${r.price}${r.show_price ? "" : " (preço oculto)"}`}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Btn onClick={() => edit(r)} testid={`room-edit-btn-${r.slug}`}><Pencil size={15} /> Editar</Btn>
              <Link to={`/admin/fotos?secao=acomodacao&acomodacao=${r.id}`} data-testid={`room-photos-btn-${r.slug}`} className="h-11 px-4 inline-flex items-center gap-2 rounded-full border border-zinc-700 text-sm hover:border-[#D4AF37]"><Images size={15} /> Fotos</Link>
              <Btn onClick={() => togglePublish(r)} testid={`room-toggle-btn-${r.slug}`}>{r.published ? <><EyeOff size={15} /> Ocultar</> : <><Eye size={15} /> Publicar</>}</Btn>
              {r.published && <a href={`/acomodacoes/${r.slug}`} target="_blank" rel="noopener noreferrer" className="h-11 w-11 grid place-items-center rounded-full border border-zinc-700 hover:border-[#D4AF37]" aria-label="Ver no site" data-testid={`room-view-btn-${r.slug}`}><ExternalLink size={15} /></a>}
              <Btn onClick={() => setDeleting(r)} danger testid={`room-delete-btn-${r.slug}`}><Trash2 size={15} /></Btn>
            </div>
          </div>
        ))}
      </div>
      <RoomForm room={editing} open={open} onClose={() => setOpen(false)} onSaved={load} />
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent className="bg-[#0A0A0A] border-zinc-800 w-[92vw]">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir “{deleting?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>A acomodação e suas fotos serão removidas do site. Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-12" data-testid="room-delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="room-delete-confirm" className="h-12 bg-red-600 hover:bg-red-700 text-white">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
